import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { 
  FaServer, 
  FaCheck, 
  FaTimes, 
  FaExclamationTriangle,
  FaSearch,
  FaFilter,
  FaEye,
  FaNetworkWired
} from 'react-icons/fa';
import './ApiLogs.css';

const ApiLogs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filters, setFilters] = useState({
    method: '',
    statusCode: '',
    userType: '',
    isSuccessful: '',
    isSuspicious: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  // Redirect if not admin
  useEffect(() => {
    if (user && user.userType !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        includeStats: 'true', // Request stats in same call
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });

      // Single API call that returns both logs and stats
      const response = await api.get(`/api/api-logs?${queryParams}`);
      
      setLogs(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
      
      // Set stats from the same response
      if (response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch API logs:', error);
      toast.error('Failed to load API logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1
  };

  const clearFilters = () => {
    setFilters({
      method: '',
      statusCode: '',
      userType: '',
      isSuccessful: '',
      isSuspicious: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const applyQuickFilter = (filterType) => {
    clearFilters();
    switch (filterType) {
      case 'failed':
        setFilters(prev => ({ ...prev, isSuccessful: 'false' }));
        break;
      case 'suspicious':
        setFilters(prev => ({ ...prev, isSuspicious: 'true' }));
        break;
      case 'errors':
        setFilters(prev => ({ ...prev, statusCode: '500' }));
        break;
      case 'unauthorized':
        setFilters(prev => ({ ...prev, statusCode: '401' }));
        break;
      default:
        break;
    }
  };

  const getMethodBadgeClass = (method) => {
    switch (method) {
      case 'GET': return 'badge-info';
      case 'POST': return 'badge-success';
      case 'PUT': return 'badge-warning';
      case 'PATCH': return 'badge-warning';
      case 'DELETE': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getStatusBadgeClass = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return 'badge-success';
    if (statusCode >= 300 && statusCode < 400) return 'badge-info';
    if (statusCode >= 400 && statusCode < 500) return 'badge-warning';
    if (statusCode >= 500) return 'badge-danger';
    return 'badge-secondary';
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading && logs.length === 0) {
    return <Layout><div className="loading">Loading API logs...</div></Layout>;
  }

  return (
    <Layout>
      <div className="api-logs-page">
        <div className="page-header">
          <div>
            <h1><FaServer /> API Logs</h1>
            <p>Monitor and analyze API requests and system activity</p>
          </div>
        </div>

        {stats && (
          <div className="stats-grid">
            <div className="stat-card card">
              <div className="stat-icon total-icon">
                <FaNetworkWired />
              </div>
              <div className="stat-content">
                <h3>Total Requests</h3>
                <p className="stat-number">{stats.totalRequests?.toLocaleString() || 0}</p>
              </div>
            </div>

            <div className="stat-card card clickable" onClick={() => applyQuickFilter('success')}>
              <div className="stat-icon success-icon">
                <FaCheck />
              </div>
              <div className="stat-content">
                <h3>Successful</h3>
                <p className="stat-number">{stats.successfulRequests?.toLocaleString() || 0}</p>
                <small>{stats.successRate || '0'}%</small>
              </div>
            </div>

            <div className="stat-card card clickable" onClick={() => applyQuickFilter('failed')}>
              <div className="stat-icon error-icon">
                <FaTimes />
              </div>
              <div className="stat-content">
                <h3>Failed</h3>
                <p className="stat-number">{stats.failedRequests?.toLocaleString() || 0}</p>
                <small>{stats.failureRate || '0'}%</small>
              </div>
            </div>

            <div className="stat-card card clickable" onClick={() => applyQuickFilter('suspicious')}>
              <div className="stat-icon warning-icon">
                <FaExclamationTriangle />
              </div>
              <div className="stat-content">
                <h3>Suspicious</h3>
                <p className="stat-number">{stats.suspiciousRequests?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
        )}

        <div className="filters-section card">
          <div className="filters-header">
            <h3><FaFilter /> Filters</h3>
            <button className="btn btn-outline-sm" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Search</label>
              <div className="search-input">
                <FaSearch />
                <input
                  type="text"
                  placeholder="URL, endpoint, email, IP..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>
            <div className="filter-group">
              <label>Method</label>
              <select 
                value={filters.method}
                onChange={(e) => handleFilterChange('method', e.target.value)}
              >
                <option value="">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select 
                value={filters.statusCode}
                onChange={(e) => handleFilterChange('statusCode', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="200">200 OK</option>
                <option value="201">201 Created</option>
                <option value="400">400 Bad Request</option>
                <option value="401">401 Unauthorized</option>
                <option value="403">403 Forbidden</option>
                <option value="404">404 Not Found</option>
                <option value="429">429 Too Many Requests</option>
                <option value="500">500 Server Error</option>
              </select>
            </div>
            <div className="filter-group">
              <label>User Type</label>
              <select 
                value={filters.userType}
                onChange={(e) => handleFilterChange('userType', e.target.value)}
              >
                <option value="">All Users</option>
                <option value="admin">Admin</option>
                <option value="support">Support</option>
                <option value="seller">Seller</option>
                <option value="buyer">Buyer</option>
                <option value="guest">Guest</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Result</label>
              <select 
                value={filters.isSuccessful}
                onChange={(e) => handleFilterChange('isSuccessful', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Successful</option>
                <option value="false">Failed</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Security</label>
              <select 
                value={filters.isSuspicious}
                onChange={(e) => handleFilterChange('isSuspicious', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Suspicious Only</option>
                <option value="false">Normal</option>
              </select>
            </div>
          </div>
          <div className="filter-results">
            Showing <strong>{logs.length}</strong> of <strong>{pagination.total.toLocaleString()}</strong> logs
          </div>
        </div>

        <div className="logs-table-section card">
          <h3>Request Logs</h3>
          {logs.length === 0 ? (
            <div className="empty-state">
              <FaServer style={{ fontSize: '48px', color: '#d1d5db' }} />
              <p>No logs found matching your filters</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Method</th>
                      <th>Endpoint</th>
                      <th>Status</th>
                      <th>User</th>
                      <th>IP Address</th>
                      <th>Duration</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log._id} className={log.isSuspicious ? 'suspicious-row' : ''}>
                        <td className="timestamp-cell">
                          {formatDate(log.timestamp)}
                        </td>
                        <td>
                          <span className={`badge ${getMethodBadgeClass(log.method)}`}>
                            {log.method}
                          </span>
                        </td>
                        <td className="endpoint-cell">
                          <div className="endpoint-content">
                            {log.endpoint}
                            {log.isSuspicious && (
                              <FaExclamationTriangle className="suspicious-icon" title="Suspicious activity detected" />
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(log.statusCode)}`}>
                            {log.statusCode}
                          </span>
                        </td>
                        <td>
                          {log.userId ? (
                            <div className="user-info">
                              <div className="user-email">{log.userEmail || log.userId.email}</div>
                              <div className="user-type">{log.userType}</div>
                            </div>
                          ) : (
                            <span className="guest-user">Guest</span>
                          )}
                        </td>
                        <td className="ip-cell">{log.ipAddress}</td>
                        <td className="duration-cell">
                          {log.responseTime ? formatDuration(log.responseTime) : '-'}
                        </td>
                        <td>
                          <button 
                            className="btn btn-icon"
                            onClick={() => setSelectedLog(log)}
                            title="View details"
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.pages > 1 && (
                <div className="pagination">
                  <button 
                    className="btn btn-outline-sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button 
                    className="btn btn-outline-sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Details Modal */}
        {selectedLog && (
          <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
            <div className="modal-content log-details-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Request Details</h3>
                <button className="close-btn" onClick={() => setSelectedLog(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="detail-section">
                  <h4>Request Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Timestamp:</label>
                      <span>{formatDate(selectedLog.timestamp)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Method:</label>
                      <span className={`badge ${getMethodBadgeClass(selectedLog.method)}`}>
                        {selectedLog.method}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Status Code:</label>
                      <span className={`badge ${getStatusBadgeClass(selectedLog.statusCode)}`}>
                        {selectedLog.statusCode}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Response Time:</label>
                      <span>{selectedLog.responseTime ? formatDuration(selectedLog.responseTime) : 'N/A'}</span>
                    </div>
                  </div>
                  <div className="detail-item full-width">
                    <label>URL:</label>
                    <code>{selectedLog.url}</code>
                  </div>
                  <div className="detail-item full-width">
                    <label>Endpoint:</label>
                    <code>{selectedLog.endpoint}</code>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>User Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>User Type:</label>
                      <span>{selectedLog.userType}</span>
                    </div>
                    <div className="detail-item">
                      <label>Email:</label>
                      <span>{selectedLog.userEmail || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>IP Address:</label>
                      <span>{selectedLog.ipAddress}</span>
                    </div>
                    <div className="detail-item">
                      <label>User Agent:</label>
                      <span className="truncate">{selectedLog.userAgent || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {selectedLog.suspiciousReasons && selectedLog.suspiciousReasons.length > 0 && (
                  <div className="detail-section alert-section">
                    <h4><FaExclamationTriangle /> Suspicious Activity Detected</h4>
                    <ul className="suspicious-reasons">
                      {selectedLog.suspiciousReasons.map((reason, index) => (
                        <li key={index}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedLog.requestHeaders && (
                  <div className="detail-section">
                    <h4>Request Headers</h4>
                    <pre className="code-block">
                      {JSON.stringify(selectedLog.requestHeaders, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.requestBody && (
                  <div className="detail-section">
                    <h4>Request Body</h4>
                    <pre className="code-block">
                      {JSON.stringify(selectedLog.requestBody, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.responseBody && (
                  <div className="detail-section">
                    <h4>Response Body</h4>
                    <pre className="code-block">
                      {JSON.stringify(selectedLog.responseBody, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setSelectedLog(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ApiLogs;
